<template>
<section v-if="shouldRender" id="workflow" class="bg-white py-20">
  <div class="mx-auto w-full max-w-6xl px-6">
    <div class="grid gap-12 md:grid-cols-[0.9fr_1.1fr] md:items-center">
      <div>
        <span v-if="badge" class="badge">{{ badge }}</span>
        <h2 v-if="heading" class="section-title mt-6">{{ heading }}</h2>
        <p v-if="description" class="section-subtitle">
          {{ description }}
        </p>
        <dl v-if="highlights.length" class="mt-10 space-y-6">
          <div
            v-for="item in highlights"
            :key="item.title || item.description"
            class="flex gap-4 rounded-2xl border border-slate-100 bg-slate-25 p-4"
          >
            <div class="icon-circle mt-1 h-10 w-10 bg-white text-hilab">
                <svg
                  v-if="item.icon === 'live'"
                  class="h-5 w-5"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M5 12h14m-7-7v14" />
                </svg>
                <svg
                  v-else-if="item.icon === 'collab'"
                  class="h-5 w-5"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M7 8h10M7 12h10M7 16h6" />
                </svg>
                <svg
                  v-else
                  class="h-5 w-5"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="m4 12 4 4 12-12" />
                </svg>
              </div>
              <div>
                <dt class="text-lg font-semibold text-slate-900">{{ item.title }}</dt>
                <dd class="mt-2 text-sm text-slate-500">{{ item.description }}</dd>
              </div>
            </div>
          </dl>
        </div>
        <div class="relative">
          <div class="absolute -inset-6 -z-10 rounded-3xl bg-gradient-to-br from-hilab/10 via-white to-white blur-2xl"></div>
          <div class="overflow-hidden rounded-3xl border border-slate-100 bg-white shadow-xl">
            <div v-if="cardTitle || cardStatus" class="flex items-center justify-between border-b border-slate-100 px-6 py-4">
              <div>
                <p class="text-xs uppercase tracking-[0.2em] text-slate-400">Customer timeline</p>
                <p v-if="cardTitle" class="mt-1 text-lg font-semibold text-slate-900">{{ cardTitle }}</p>
              </div>
              <span v-if="cardStatus" :class="['rounded-full px-3 py-1 text-xs font-semibold', statusClass]">
                {{ cardStatus }}
              </span>
            </div>
            <div v-if="timeline.length" class="space-y-0.5 bg-slate-25/60 p-6">
              <div v-for="event in timeline" :key="event.time || event.title" class="relative flex gap-4 rounded-2xl bg-white/90 p-4 shadow-sm">
                <div class="flex h-10 w-10 items-center justify-center rounded-full bg-hilab/10 text-hilab">
                  <svg
                    v-if="event.icon === 'chat'"
                    class="h-5 w-5"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M7 8h10M7 12h6m-6 4h4" />
                  </svg>
                  <svg
                    v-else-if="event.icon === 'note'"
                    class="h-5 w-5"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4 7h16M4 12h8m-8 5h8m4.5-8.5L20 5v5.5h-3.5z" />
                  </svg>
                  <svg
                    v-else
                    class="h-5 w-5"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M3 12h18m-9-9v18" />
                  </svg>
                </div>
                <div class="flex-1">
                  <div class="flex items-center justify-between text-xs text-slate-400">
                    <span>{{ event.time }}</span>
                    <span>{{ event.channel }}</span>
                  </div>
                  <p v-if="event.title" class="mt-2 text-sm font-semibold text-slate-900">{{ event.title }}</p>
                  <p v-if="event.description" class="mt-1 text-xs text-slate-500">{{ event.description }}</p>
                </div>
                <span v-if="event.score" class="self-start rounded-full bg-hilab/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-hilab">{{ event.score }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  content: {
    type: Object,
    default: () => ({}),
  },
});

const toArray = value => (Array.isArray(value) ? value : value && typeof value === 'object' ? Object.values(value) : []);

const sectionContent = computed(() => props.content || {});
const badge = computed(() => sectionContent.value.badge || '');
const heading = computed(() => sectionContent.value.heading || '');
const description = computed(() => sectionContent.value.description || '');
const highlights = computed(() =>
  toArray(sectionContent.value.highlights).filter(item => item?.title || item?.description)
);
const timeline = computed(() =>
  toArray(sectionContent.value.timeline).filter(event => event?.time || event?.title || event?.description)
);
const card = computed(() => sectionContent.value.card || {});
const cardTitle = computed(() => card.value.title || '');
const cardStatus = computed(() => card.value.status || '');
const statusVariant = computed(() => card.value.status_variant || 'default');
const statusClass = computed(() => {
  switch (statusVariant.value) {
    case 'success':
      return 'bg-green-100 text-green-600';
    case 'warning':
      return 'bg-amber-100 text-amber-600';
    case 'danger':
      return 'bg-red-100 text-red-600';
    default:
      return 'bg-slate-100 text-slate-600';
  }
});
const shouldRender = computed(
  () => heading.value || description.value || highlights.value.length || timeline.value.length || cardTitle.value || cardStatus.value
);
</script>
